// Localized menu-item and category display names (take-order-i18n R9.1-R9.5).
//
// Mirrors admin-mobile-app/src/utils/menuName.js. Keep the two identical.
//
// THE CENTRAL RULE (R7.2 / R9.6):
//   `ProductName` is the authoritative ENGLISH name and the only name ever
//   submitted with an order. Everything here is DISPLAY-ONLY. A translated name
//   must never reach the server, or catalog re-pricing, POS provider sync, reports
//   and receipts would all start seeing a name they cannot match.
//
// Every function tolerates a missing, null or malformed `ProductNameI18n`, because
// the overwhelming majority of catalog rows will never have one and must render
// exactly as they did before this feature (R1.9, design Property 5/7).

import { DEFAULT_LANGUAGE, languageCollation } from './i18nRegistry';

// Read a translation map defensively. Returns {} for anything unusable.
function translationsOf(row) {
  const map = row && typeof row.ProductNameI18n === 'object' && row.ProductNameI18n !== null
    ? row.ProductNameI18n
    : null;
  return Array.isArray(map) ? {} : (map || {});
}

/** The unchanged English name, with the same fallback chain as before this feature. */
export function englishName(row) {
  const raw = String((row && (row.ProductName ?? row.name)) || '').trim();
  return raw || 'Item';
}

/**
 * Display name for a menu item in `lang`.
 * Falls back to the English `ProductName` when there is no usable translation,
 * so a row without translations is indistinguishable from pre-feature (R9.2).
 */
export function localizedName(row, lang) {
  const code = String(lang || '').trim().toLowerCase();
  if (code && code !== DEFAULT_LANGUAGE) {
    const value = translationsOf(row)[code];
    const text = typeof value === 'string' ? value.trim() : '';
    if (text) return text;
  }
  return englishName(row);
}

/**
 * Display name for a category chip.
 *
 * Categories are looked up by DISPLAY NAME, not by id: a product row carries its
 * category's name in `Category`, so that string is the only join key available on
 * the client. See the note in lambda/pos/admin.py for why.
 */
export function localizedCategoryName(categoryName, categoryNameI18n, lang) {
  const name = String(categoryName || '').trim();
  const code = String(lang || '').trim().toLowerCase();
  if (!name || !code || code === DEFAULT_LANGUAGE) return name;

  const table = categoryNameI18n && typeof categoryNameI18n === 'object' ? categoryNameI18n : {};
  const entry = table[name];
  const map = entry && typeof entry === 'object' && !Array.isArray(entry) ? entry : {};
  const text = typeof map[code] === 'string' ? map[code].trim() : '';
  return text || name;
}

/**
 * Does a menu item match a search query in `lang`?
 *
 * Matches the UNION of the localized name and the English `ProductName` (R9.4).
 * The English half is not redundant: a Tamil-speaking server may still know a dish
 * by the English name printed on the menu, and matching both guarantees Tamil mode
 * can never hide an item that English mode would have found.
 */
export function matchesSearch(row, query, lang) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const candidates = [englishName(row), localizedName(row, lang)];
  return candidates.some((c) => c.toLowerCase().includes(q));
}

/**
 * Comparator for category chips, ordered by their DISPLAYED name.
 *
 * Wrapped in try/catch because React Native's Hermes may ship without full ICU
 * collation data, in which case `localeCompare` with a locale argument throws.
 * Same defensive pattern as utils/money.js.
 */
export function compareCategoryNames(a, b, categoryNameI18n, lang) {
  const left = localizedCategoryName(a, categoryNameI18n, lang);
  const right = localizedCategoryName(b, categoryNameI18n, lang);
  try {
    return left.localeCompare(right, languageCollation(lang));
  } catch {
    return left.localeCompare(right);
  }
}
